import React from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactCTA from '../components/ContactCTA';

const GovernmentContracts = () => {
  const values = [
    {
      title: 'Expert Insights. Tailored Solutions. Unmatched Results.',
      description: 'We deliver tailored solutions that directly address the unique challenges faced by state and federal agencies.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      title: 'We infuse state and federal expertise in our solutions.',
      description: 'We know health and social safety net programs from the micro to macro level. Our strategies span policy and operations, accounting for impact on stakeholders at every level.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      title: 'We are trusted advisors.',
      description: 'We collaborate with agencies to develop guidance and strategy. Our partners trust us to represent their best interests, deliver insights, and work diligently to achieve goals.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      title: 'We are committed to improving health outcomes.',
      description: 'We bring a health and socioeconomic lens to every engagement, ensuring that our work advances health outcomes for individuals, families, and communities.',
      icon: (
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
    },
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="hero-section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl md:text-6xl text-accent">
              Strategic Partnerships for State & Federal Agencies
            </h1>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Onyx Rose Advisors LLC is a minority, woman-owned firm supporting state and federal health and human services agencies. We serve as partners in developing strategies and shaping policies to improve access, quality, and health outcomes for those they serve.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Just like assembling a complex puzzle, we ensure every piece—strategic planning, policy development, program implementation, and evaluation—fits together seamlessly.
              </p>
              <a
                href="/assets/ORA-Capability-Statement-March-2026.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-accent text-white font-semibold px-10 py-4 rounded-md hover:bg-accent-900 transition-all duration-200 shadow-lg hover:shadow-xl text-lg"
              >
                View Capability Statement
              </a>
            </div>

            {/* Image */}
            <div className="flex justify-center lg:justify-end">
              <img
                src="/assets/img/puzzle.jpeg"
                alt="Puzzle pieces representing strategic collaboration"
                className="w-full max-w-lg rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mb-6">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Contractor Info Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Certifications & Contractor Information
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We are proud to be a certified minority, woman-owned small business ready to support your agency's mission.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Certifications */}
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Certifications</h3>
              <div className="flex flex-col items-center gap-6">
                <div className="flex items-center gap-4">
                  <img src="/assets/edwosb-certification.png" alt="EDWOSB Certified" className="h-20 w-auto" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">EDWOSB Certified</p>
                    <p className="text-sm text-gray-600">Economically Disadvantaged Woman Owned Small Business</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <img src="/assets/swam-certification.png" alt="SWAM Certified" className="h-20 w-auto" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">SWAM Certified</p>
                    <p className="text-sm text-gray-600">Small, Woman, and Minority-owned Business</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Federal Contractor ID */}
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Federal Contractor ID</h3>
              <div className="space-y-4 text-left">
                <div className="flex justify-between items-center border-b border-gray-300 pb-3">
                  <span className="font-semibold text-gray-700">UEI:</span>
                  <span className="text-gray-900 font-mono">Y52BX7LQGEV5</span>
                </div>
                <div className="flex justify-between items-center border-b border-gray-300 pb-3">
                  <span className="font-semibold text-gray-700">CAGE:</span>
                  <span className="text-gray-900 font-mono">9XKP0</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-gray-700">DUNS:</span>
                  <span className="text-gray-900 font-mono">13-239-8048</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA
        title="Ready to Partner With Us?"
        description="Contact us today to learn how Onyx Rose Advisors can support your agency's mission and objectives."
      />

      <Footer />
    </div>
  );
};

export default GovernmentContracts;
